import { useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { caricaElenco } from '../impianti/impianti.api'
import { avviaDownload } from '../dichiarazione/download.api'
import {
  caricaRegistro, chiediRegistro, elencaRegistri, inviaAlCliente, scaricaRegistro, type Registro, type StatoRegistro,
} from './registri.api'

const NESSUNO: Registro[] = []

const ETICHETTA_STATO: Record<StatoRegistro, string> = {
  in_coda: 'In preparazione',
  pronto: 'Pronto',
  errore: 'Non riuscito',
}

const COLORE_STATO: Record<StatoRegistro, 'default' | 'success' | 'error'> = {
  in_coda: 'default',
  pronto: 'success',
  errore: 'error',
}

export function Registri() {
  const queryClient = useQueryClient()
  // Generare e caricare chiedono gli stessi dati (impianto e anno): un solo
  // dialogo, e `file` c'e' solo quando si carica.
  const [richiesta, setRichiesta] = useState<{
    modo: 'genera' | 'carica'
    impiantoId: number
    anno: string
    file: File | null
  } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [daInviare, setDaInviare] = useState<Registro | null>(null)

  const { data: registri = NESSUNO } = useQuery({
    queryKey: ['registri'],
    queryFn: elencaRegistri,
    // PDF e email li fa il worker: finche' uno dei due e' in coda si ricontrolla.
    refetchInterval: (q) =>
      q.state.data?.some((r) => r.stato === 'in_coda' || r.ultimoInvio?.stato === 'in_coda') ? 5000 : false,
  })
  const { data: impianti = [] } = useQuery({ queryKey: ['impianti', null, null], queryFn: () => caricaElenco(null) })

  async function suConferma() {
    if (richiesta === null) return
    const anno = Number(richiesta.anno)
    if (!Number.isInteger(anno) || anno < 2000 || anno > 2100) {
      setErrore('Anno non valido')
      return
    }
    if (richiesta.modo === 'carica' && richiesta.file === null) {
      setErrore('Scegli il file del registro')
      return
    }
    setErrore(null)
    try {
      if (richiesta.modo === 'carica') await caricaRegistro(richiesta.impiantoId, anno, richiesta.file!)
      else await chiediRegistro(richiesta.impiantoId, anno)
      setRichiesta(null)
      await queryClient.invalidateQueries({ queryKey: ['registri'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suScarica(r: Registro) {
    setErrore(null)
    try {
      avviaDownload(await scaricaRegistro(r.id))
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Registri delle letture
      </Typography>
      {errore !== null && richiesta === null && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{errore}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Impianto</TableCell>
            <TableCell>Anno</TableCell>
            <TableCell>Stato</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {registri.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.impianto}</TableCell>
              <TableCell>{r.anno}</TableCell>
              <TableCell>
                {r.origine === 'caricato' ? (
                  <>
                    <Chip size="small" label="Caricato" color="success" variant="outlined" />
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {r.nomeFile}
                    </Typography>
                  </>
                ) : (
                  <Chip size="small" label={ETICHETTA_STATO[r.stato]} color={COLORE_STATO[r.stato]} />
                )}
                <EsitoInvio registro={r} />
                {r.errore !== null && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    {r.errore}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {r.stato === 'pronto' && (
                  <Button
                    size="small"
                    aria-label={`Scarica il registro ${r.anno} di ${r.impianto}`}
                    onClick={() => void suScarica(r)}
                  >
                    Scarica
                  </Button>
                )}
                {r.stato === 'pronto' && (
                  <Button
                    size="small"
                    aria-label={`Invia al cliente il registro ${r.anno} di ${r.impianto}`}
                    onClick={() => setDaInviare(r)}
                  >
                    Invia al cliente
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {registri.length === 0 && <p>Nessun registro generato</p>}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {(['genera', 'carica'] as const).map((modo) => (
          <Button
            key={modo}
            variant="outlined"
            disabled={impianti.length === 0}
            onClick={() => {
              setErrore(null)
              setRichiesta({ modo, impiantoId: impianti[0]!.id, anno: String(new Date().getFullYear()), file: null })
            }}
          >
            {modo === 'genera' ? 'Genera registro' : 'Carica un registro esistente'}
          </Button>
        ))}
      </Stack>

      <Dialog open={richiesta !== null} onClose={() => setRichiesta(null)} fullWidth maxWidth="sm">
        <DialogTitle>{richiesta?.modo === 'carica' ? 'Carica un registro esistente' : 'Nuovo registro'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            <TextField
              select
              size="small"
              label="Impianto"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={richiesta?.impiantoId ?? ''}
              onChange={(e) => setRichiesta((r) => (r === null ? r : { ...r, impiantoId: Number(e.target.value) }))}
            >
              {impianti.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.ragioneSociale} ({i.codiceDichiarazione})
                </option>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Anno"
              value={richiesta?.anno ?? ''}
              onChange={(e) => setRichiesta((r) => (r === null ? r : { ...r, anno: e.target.value }))}
            />
            {richiesta?.modo === 'carica' ? (
              <>
                <Button component="label" variant="outlined">
                  {richiesta.file?.name ?? 'Scegli il file (PDF o Excel)'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.xlsx,.xls"
                    aria-label="File del registro"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      setRichiesta((r) => (r === null ? r : { ...r, file }))
                    }}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Il file viene archiviato cosi com e, fino a 20 MB: le letture non vengono lette dal file.
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Il registro lo compone il server: compare in elenco come «In preparazione» e diventa scaricabile
                appena e pronto.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRichiesta(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suConferma()}>
            {richiesta?.modo === 'carica' ? 'Carica' : 'Genera'}
          </Button>
        </DialogActions>
      </Dialog>
      {daInviare !== null && (
        <DialogoInvio
          registro={daInviare}
          onChiudi={async (inviato) => {
            setDaInviare(null)
            if (inviato) await queryClient.invalidateQueries({ queryKey: ['registri'] })
          }}
        />
      )}
    </Box>
  )
}

function EsitoInvio({ registro }: { registro: Registro }) {
  const invio = registro.ultimoInvio
  if (invio === null) return null
  const testo =
    invio.stato === 'inviata'
      ? `Inviato al cliente il ${new Date(invio.creatoIl).toLocaleDateString('it-IT')}`
      : invio.stato === 'in_coda'
        ? 'Invio al cliente in corso'
        : `Invio al cliente non riuscito: ${invio.errore ?? 'errore sconosciuto'}`
  return (
    <Typography variant="caption" color={invio.stato === 'errore' ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
      {testo}
    </Typography>
  )
}

/** Conferma con l'indirizzo sotto gli occhi: il registro parte all'email
 * dell'azienda, e un indirizzo vecchio in anagrafica si nota qui, non dopo. */
function DialogoInvio({ registro, onChiudi }: { registro: Registro; onChiudi: (inviato: boolean) => Promise<void> }) {
  const [errore, setErrore] = useState<string | null>(null)
  return (
    <Dialog open onClose={() => void onChiudi(false)} fullWidth maxWidth="sm">
      <DialogTitle>Invia il registro al cliente</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
          {registro.emailCliente === null ? (
            <Typography>
              L azienda di {registro.impianto} non ha un indirizzo email: aggiungilo nell anagrafica dell azienda.
            </Typography>
          ) : (
            <Typography>
              Il registro {registro.anno} di {registro.impianto} parte in allegato a {registro.emailCliente}, con il
              modello «richiesta_vidimazione».
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void onChiudi(false)}>Annulla</Button>
        <Button
          variant="contained"
          disabled={registro.emailCliente === null}
          onClick={async () => {
            setErrore(null)
            try {
              await inviaAlCliente(registro.id)
              await onChiudi(true)
            } catch (e) {
              setErrore(e instanceof Error ? e.message : 'errore imprevisto')
            }
          }}
        >
          Invia
        </Button>
      </DialogActions>
    </Dialog>
  )
}
